// Тактические факты из PV движка (без LLM, без DOM). Прокручиваем главную линию
// через chess.js и достаём надёжные мотивы: материальный итог линии, первые ходы
// (SAN), самую ценную снятую фигуру, форсированный мат, вилку и back-rank мат.
// Принцип: при сомнении молчим (враный факт хуже отсутствия). Покрыто vitest.
import { Chess, type Square } from 'chess.js';
import { uciToMove } from '@ext/core/eval-utils';
import { PIECE_VALUES } from '@ext/core/hanging';

export interface Fork {
  sq1: string;
  sq2: string;
}

export interface PvFacts {
  /** Материальный итог линии в пешках с точки зрения стороны хода в `fen`. */
  materialDelta: number;
  /** Первые (до 3) ходы линии в SAN. */
  firstMoves: string[];
  /** Тип самой ценной снятой фигуры соперника (буква) или null. */
  capturedPiece: string | null;
  /** Сторона хода форсирует мат в N (иначе null). */
  givesMateIn: number | null;
  /** Вилка сразу после первого хода линии (или null). */
  fork: Fork | null;
  /** Форсированный мат — по последней горизонтали. */
  backRank: boolean;
}

const EMPTY: PvFacts = {
  materialDelta: 0,
  firstMoves: [],
  capturedPiece: null,
  givesMateIn: null,
  fork: null,
  backRank: false,
};

type Color = 'w' | 'b';

function sq(square: string): { f: number; r: number } | null {
  const f = 'abcdefgh'.indexOf(square?.[0] ?? '');
  const r = Number(square?.[1]) - 1;
  return f >= 0 && r >= 0 && r < 8 ? { f, r } : null;
}

function coord(f: number, r: number): Square | null {
  return f >= 0 && f < 8 && r >= 0 && r < 8 ? (('abcdefgh'[f]! + String(r + 1)) as Square) : null;
}

/** Геометрическая атака фигуры `piece` из `from` на `to` (без учёта своего короля). */
function attacks(chess: Chess, from: string, to: string, type: string, color: Color): boolean {
  if (from === to) return false;
  const a = sq(from);
  const b = sq(to);
  if (!a || !b) return false;
  const df = b.f - a.f;
  const dr = b.r - a.r;
  if (type === 'p') return Math.abs(df) === 1 && dr === (color === 'w' ? 1 : -1);
  if (type === 'n') return (Math.abs(df) === 1 && Math.abs(dr) === 2) || (Math.abs(df) === 2 && Math.abs(dr) === 1);
  if (type === 'k') return Math.abs(df) <= 1 && Math.abs(dr) <= 1;
  if (type === 'b' && Math.abs(df) !== Math.abs(dr)) return false;
  if (type === 'r' && df !== 0 && dr !== 0) return false;
  if (type === 'q' && !(Math.abs(df) === Math.abs(dr) || df === 0 || dr === 0)) return false;
  const stepF = Math.sign(df);
  const stepR = Math.sign(dr);
  let f = a.f + stepF;
  let r = a.r + stepR;
  while (f !== b.f || r !== b.r) {
    const c = coord(f, r);
    if (!c || chess.get(c)) return false;
    f += stepF;
    r += stepR;
  }
  return true;
}

/** Вилка: одна фигура `color` бьёт ≥2 фигуры соперника ценнее пешки (шах королю
 *  считается атакой). Надёжность: одна из целей тяжёлая (ладья/ферзь/король) и
 *  атакующий дешевле — иначе молчим. */
function detectFork(chess: Chess, color: Color): Fork | null {
  const enemy: Color = color === 'w' ? 'b' : 'w';
  for (const row of chess.board()) {
    for (const p of row) {
      if (!p || p.color !== color || p.type === 'k' || p.type === 'p') continue;
      const attackerVal = PIECE_VALUES[p.type] ?? 0;
      const targets: { sq: string; val: number }[] = [];
      for (const erow of chess.board()) {
        for (const e of erow) {
          if (!e || e.color !== enemy || e.type === 'p') continue;
          if (!attacks(chess, p.square, e.square, p.type, color)) continue;
          targets.push({ sq: e.square, val: e.type === 'k' ? 100 : PIECE_VALUES[e.type] ?? 0 });
        }
      }
      if (targets.length < 2) continue;
      targets.sort((x, y) => y.val - x.val);
      const [t1, t2] = targets;
      const heavy = t1!.val >= 5 || t2!.val >= 5; // король=100 тоже тяжёлый
      const winning = t2!.val > attackerVal || t1!.val >= 100;
      if (heavy && winning) return { sq1: t1!.sq, sq2: t2!.sq };
    }
  }
  return null;
}

/** Мат по последней горизонтали: заматованный король на своей крайней линии,
 *  и там же ладья/ферзь матующей стороны. */
function isBackRankMate(chess: Chess, color: Color): boolean {
  const enemy: Color = color === 'w' ? 'b' : 'w';
  const rank = enemy === 'w' ? 0 : 7; // 0-based: белые — 1-я, чёрные — 8-я
  let king: { f: number; r: number } | null = null;
  for (const row of chess.board()) {
    for (const p of row) {
      if (p && p.color === enemy && p.type === 'k') {
        const c = sq(p.square);
        if (c) king = c;
      }
    }
  }
  if (!king || king.r !== rank) return false;
  for (const row of chess.board()) {
    for (const p of row) {
      if (p && p.color === color && (p.type === 'r' || p.type === 'q')) {
        const c = sq(p.square);
        if (c && c.r === rank) return true;
      }
    }
  }
  return false;
}

/** Факты по линии `pv` (UCI-ходы) из позиции `fen`. Всё завёрнуто в try — при любой
 *  нелегальщине возвращаем пустые факты (молчим). */
export function pvFacts(fen: string, pv: string[] | null | undefined): PvFacts {
  const moves = pv || [];
  if (!moves.length) return EMPTY;
  let chess: Chess;
  try {
    chess = new Chess(fen);
  } catch {
    return EMPTY;
  }
  const start = chess.turn() as Color;
  const gain: Record<Color, number> = { w: 0, b: 0 };
  const firstMoves: string[] = [];
  let capturedPiece: string | null = null;
  let capturedVal = 0;
  let mateIn: number | null = null;
  let afterFirst: Chess | null = null;
  let idx = 0;
  for (const uci of moves) {
    const mv = uciToMove(uci);
    if (!mv) break;
    const mover = chess.turn() as Color;
    let made;
    try {
      made = chess.move(mv);
    } catch {
      made = null;
    }
    if (!made) break;
    if (idx === 0) {
      try {
        afterFirst = new Chess(made.after);
      } catch {
        afterFirst = null;
      }
    }
    if (firstMoves.length < 3) firstMoves.push(made.san);
    if (made.captured) {
      const v = PIECE_VALUES[made.captured] ?? 0;
      gain[mover] += v;
      if (mover === start && v > capturedVal) {
        capturedVal = v;
        capturedPiece = made.captured;
      }
    }
    if (made.promotion) gain[mover] += (PIECE_VALUES[made.promotion] ?? 0) - 1;
    if (chess.isCheckmate()) {
      if (idx % 2 === 0) mateIn = idx / 2 + 1; // мат ставит сторона хода
      break;
    }
    idx += 1;
  }
  const other: Color = start === 'w' ? 'b' : 'w';
  return {
    materialDelta: gain[start] - gain[other],
    firstMoves,
    capturedPiece,
    givesMateIn: mateIn,
    fork: afterFirst ? detectFork(afterFirst, start) : null,
    backRank: mateIn != null && isBackRankMate(chess, start),
  };
}
